/*
 * <bundle-builder> — Build-Your-Own-Bundle custom element.
 * UI derives from `this.state`; config comes from data-* attributes, never hardcoded.
 * Price maths is display only — Shopify applies the real discount at checkout.
 */
if (!customElements.get('bundle-builder')) {
  const CART_UNREACHABLE = "We couldn't reach the cart — please check your connection and try again.";

  customElements.define(
    'bundle-builder',
    class BundleBuilder extends HTMLElement {
      connectedCallback() {
        // Config from data-* — never hardcode the thresholds.
        this.discountPercentage = parseInt(this.dataset.discountPercentage, 10) || 0;
        this.minQuantity = parseInt(this.dataset.bundleMinQuantity, 10) || 0;
        this.shippingQuantity = parseInt(this.dataset.freeShippingQuantity, 10) || 0;
        // Free shipping is price-based (£ setting → pennies); progress bar/"complete" uses pots.
        this.shippingThreshold = (parseInt(this.dataset.shippingThreshold, 10) || 0) * 100;
        // Free-gift milestone (pots) — display-only; the gift itself is admin-configured.
        this.giftQuantity = parseInt(this.dataset.giftQuantity, 10) || 0;

        this.currency = this.dataset.currency || 'GBP';
        this.locale = this.dataset.locale || 'en-GB';

        this.storageKey = 'bundle-builder-state';
        // localStorage TTL — drafts must survive in-app-browser kills (Meta-ad traffic)
        // but a months-old draft shouldn't greet a returning customer.
        this.storageTtl = 7 * 24 * 60 * 60 * 1000;

        this.grid = this.querySelector('[data-bundle-grid]');
        this.panelItems = this.querySelector('[data-bundle-panel-items]');
        this.panelCount = this.querySelector('[data-bundle-count]');
        this.panelSubtotal = this.querySelector('[data-bundle-subtotal]');

        this.segments = Array.from(
          this.querySelectorAll('[data-bundle-bar] .bundle-panel__segment')
        );
        this.ctaButton = this.querySelector('[data-bundle-checkout]');
        this.ctaLabel = this.querySelector('[data-bundle-cta-label]');
        this.ctaReadyLabel =
          (this.ctaButton && this.ctaButton.dataset.ctaLabel) || 'Add bundle to cart';
        this.savingEl = this.querySelector('[data-bundle-saving]');
        this.statusAdded = this.querySelector('[data-bundle-status-added]');
        this.statusRemaining = this.querySelector('[data-bundle-status-remaining]');
        this.statusReady = this.querySelector('[data-bundle-status-ready]');
        this.statusShipping = this.querySelector('[data-bundle-status-shipping]');
        this.milestones = Array.from(this.querySelectorAll('[data-bundle-milestone]'));

        // Original card order feeds the "best selling" sort.
        this.sortSelect = this.querySelector('[data-bundle-sort]');
        this.bestsellerTile = this.grid && this.grid.querySelector('[data-bundle-bestseller-tile]');
        this.originalCardOrder = this.grid
          ? Array.from(this.grid.querySelectorAll('[data-product-id]'))
          : [];
        this.activeFilters = new Set();

        // Mobile bottom tray (a <details>) + drawer state.
        this.panelDisclosure = this.querySelector('[data-bundle-panel-disclosure]');
        this.trayQuery = window.matchMedia('(max-width: 989px)');
        this.activeDrawer = null;
        this.drawerOpener = null;
        this.submitting = false;

        // Catalog: product id → product data.
        this.catalog = new Map();
        this.buildCatalog();

        // State: the single source of truth.
        this.state = {
          items: [], // [{ id, variantId, quantity }]
          bestsellerCount: 0, // best-seller sets added via the tile (its stepper quantity)
          minQuantity: this.minQuantity,
          shippingQuantity: this.shippingQuantity,
        };

        // One delegated click handler routes every control; change drives the sort <select>.
        this.boundClick = this.handleClick.bind(this);
        this.addEventListener('click', this.boundClick);
        this.boundChange = this.handleChange.bind(this);
        this.addEventListener('change', this.boundChange);
        this.boundKeydown = this.handleKeydown.bind(this);
        this.addEventListener('keydown', this.boundKeydown);
        this.boundTrayBreakpoint = this.handleTrayBreakpoint.bind(this);
        this.trayQuery.addEventListener('change', this.boundTrayBreakpoint);
        // Click-away dismiss bound directly on each drawer root (not delegated) so stopped
        // propagation can't break it.
        this.boundDrawerDismiss = (event) => {
          if (!event.target.closest('.bundle-drawer__panel')) this.closeDrawer();
        };
        this.drawerRoots = Array.from(this.querySelectorAll('[data-bundle-drawer]'));
        this.drawerRoots.forEach((drawer) =>
          drawer.addEventListener('click', this.boundDrawerDismiss)
        );
        // Timestamp tray toggles to suppress the iOS "ghost click" that can land on the CTA.
        this.boundTrayToggle = () => {
          this.lastTrayToggle = Date.now();
        };
        if (this.panelDisclosure) {
          this.panelDisclosure.addEventListener('toggle', this.boundTrayToggle);
        }

        // Card banner shrink/grow — hover-only, gated per card by width (matches the CSS
        // @container breakpoint); re-sized on resize.
        this.activeCard = null;
        this.canHoverCards = window.matchMedia('(hover: hover)').matches;
        this.cardBannerMinWidth = 260; // keep in sync with the @container breakpoint in the CSS
        if (this.grid && this.canHoverCards) {
          this.boundCardEnter = this.handleCardEnter.bind(this);
          this.boundCardLeave = this.handleCardLeave.bind(this);
          this.grid.addEventListener('pointerover', this.boundCardEnter);
          this.grid.addEventListener('focusin', this.boundCardEnter);
          this.grid.addEventListener('pointerout', this.boundCardLeave);
          this.grid.addEventListener('focusout', this.boundCardLeave);
          this.boundResize = this.handleResize.bind(this);
          window.addEventListener('resize', this.boundResize);
        }

        // Restore any saved bundle before the first render.
        this.restoreState();
        this.render();

        // Mobile tray starts collapsed — never auto-opened.
        this.setInitialTrayState();
      }

      disconnectedCallback() {
        if (this.boundClick) this.removeEventListener('click', this.boundClick);
        if (this.boundChange) this.removeEventListener('change', this.boundChange);
        if (this.boundKeydown) this.removeEventListener('keydown', this.boundKeydown);
        if (this.boundTrayBreakpoint) {
          this.trayQuery.removeEventListener('change', this.boundTrayBreakpoint);
        }
        if (this.boundDrawerDismiss && this.drawerRoots) {
          this.drawerRoots.forEach((drawer) =>
            drawer.removeEventListener('click', this.boundDrawerDismiss)
          );
        }
        if (this.boundTrayToggle && this.panelDisclosure) {
          this.panelDisclosure.removeEventListener('toggle', this.boundTrayToggle);
        }
        if (this.canHoverCards && this.grid && this.boundCardEnter) {
          this.grid.removeEventListener('pointerover', this.boundCardEnter);
          this.grid.removeEventListener('focusin', this.boundCardEnter);
          this.grid.removeEventListener('pointerout', this.boundCardLeave);
          this.grid.removeEventListener('focusout', this.boundCardLeave);
          if (this.boundResize) window.removeEventListener('resize', this.boundResize);
        }
      }

      /* ---- Events ---- */

      handleClick(event) {
        const target = event.target;

        const bestsellers = target.closest('[data-bundle-add-bestsellers]');
        if (bestsellers) {
          this.addBestsellers(bestsellers);
          return;
        }

        const pill = target.closest('[data-filter]');
        if (pill) {
          this.toggleFilter(pill);
          return;
        }

        if (target.closest('[data-bundle-drawer-close]')) {
          this.closeDrawer();
          return;
        }

        const carouselControl = target.closest(
          '[data-bundle-carousel-prev], [data-bundle-carousel-next], [data-bundle-carousel-dot]'
        );
        if (carouselControl) {
          this.moveCarousel(carouselControl);
          return;
        }

        const learnMore = target.closest('[data-bundle-learn-more]');
        if (learnMore) {
          if (learnMore.dataset.drawerTarget) this.openDrawer(learnMore.dataset.drawerTarget);
          return;
        }

        if (target.closest('[data-bundle-checkout]')) {
          this.checkout();
          return;
        }

        if (target.closest('[data-bundle-increase]')) {
          const tile = target.closest('[data-bundle-bestseller-tile]');
          if (tile) this.setBestsellerCount(this.state.bestsellerCount + 1, tile);
          else this.updateQuantity(this.resolveProductId(target), 1);
        } else if (target.closest('[data-bundle-decrease]')) {
          const tile = target.closest('[data-bundle-bestseller-tile]');
          if (tile) this.setBestsellerCount(this.state.bestsellerCount - 1, tile);
          else this.updateQuantity(this.resolveProductId(target), -1);
        } else if (target.closest('[data-bundle-add]')) {
          this.addProduct(this.resolveProductId(target));
        }
      }

      /* Panel rows carry the id on [data-bundle-item], cards on [data-product-id],
       * drawers in the element id (BundleDrawer-<productId>). */
      resolveProductId(el) {
        if (!el) return null;
        const row = el.closest('[data-bundle-item]');
        if (row) return row.dataset.bundleItem;
        const card = el.closest('[data-product-id]');
        if (card) return card.dataset.productId;
        const drawer = el.closest('[data-bundle-drawer]');
        if (drawer && drawer.id.indexOf('BundleDrawer-') === 0) {
          return drawer.id.slice('BundleDrawer-'.length);
        }
        return null;
      }

      handleChange(event) {
        if (event.target.closest('[data-bundle-sort]')) {
          this.sortCards(event.target.value);
        }
      }

      handleKeydown(event) {
        if (event.key === 'Escape' && this.activeDrawer) this.closeDrawer();
      }

      /* ---- Card banner shrink/grow (hover devices only) ---- */

      // rAF-throttled so a resize drag doesn't thrash layout.
      handleResize() {
        if (this.resizeRaf) return;
        this.resizeRaf = requestAnimationFrame(() => {
          this.resizeRaf = null;
          this.collapseIdleCardBanners();
        });
      }

      handleCardEnter(event) {
        const card = event.target.closest('.bundle-card');
        if (!card || card === this.activeCard) return;
        this.activeCard = card;
        this.setCardBannerWidth(card, true, true);
      }

      handleCardLeave(event) {
        const card = event.target.closest('.bundle-card');
        if (!card || (event.relatedTarget && card.contains(event.relatedTarget))) return;
        if (this.activeCard === card) this.activeCard = null;
        this.setCardBannerWidth(card, false, true);
      }

      // Active card open, the rest collapsed — instant, resting cards must not animate.
      collapseIdleCardBanners() {
        if (!this.canHoverCards || !this.grid) return;
        this.grid.querySelectorAll('.bundle-card').forEach((card) => {
          this.setCardBannerWidth(card, card === this.activeCard, false);
        });
      }

      // Open = full banner width (grows outward from centre); closed = just the resting control.
      setCardBannerWidth(card, open, animate) {
        const banner = card.querySelector('.bundle-card__actions');
        if (!banner) return;
        // Width morph only for in-bundle cards wide enough for the banner (matches the CSS
        // @container gate); all other cards must have no inline width.
        if (card.offsetWidth < this.cardBannerMinWidth || card.dataset.inBundle !== 'true') {
          if (banner.style.width) banner.style.width = '';
          return;
        }
        let width;
        if (open) {
          const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 10;
          width = Math.max(0, banner.parentElement.clientWidth - 2.4 * rem);
        } else {
          const control = card.querySelector('.bundle-card__stepper');
          const pad = parseFloat(getComputedStyle(banner).paddingLeft) || 0;
          width = control ? control.offsetWidth + pad * 2 : 0;
        }
        if (animate) {
          banner.style.width = `${width}px`;
        } else {
          // Instant — resting cards must not animate on render.
          banner.style.transition = 'none';
          banner.style.width = `${width}px`;
          void banner.offsetWidth;
          banner.style.transition = '';
        }
      }

      /* ---- Filter + sort (visual only — the bundle state is never touched) ---- */

      // Multi-select; no active pill → show everything.
      toggleFilter(pill) {
        const category = pill.dataset.filter;
        const wasPressed = pill.getAttribute('aria-pressed') === 'true';
        pill.setAttribute('aria-pressed', String(!wasPressed));
        if (wasPressed) this.activeFilters.delete(category);
        else this.activeFilters.add(category);
        this.applyFilter();
      }

      applyFilter() {
        if (!this.grid) return;
        const none = this.activeFilters.size === 0;
        this.grid.querySelectorAll('[data-product-id]').forEach((card) => {
          card.hidden = !(none || this.activeFilters.has(card.dataset.category));
        });
      }

      // Best Sellers tile stays pinned to the 4th cell; filtering rides along untouched.
      sortCards(value) {
        if (!this.grid) return;
        const cards = Array.from(this.grid.querySelectorAll('[data-product-id]'));
        const priceOf = (card) => parseInt(card.dataset.price, 10) || 0;
        const titleOf = (card) => {
          const el = card.querySelector('.bundle-card__title');
          return el ? el.textContent.trim() : '';
        };

        let ordered;
        switch (value) {
          case 'price-ascending':
            ordered = cards.slice().sort((a, b) => priceOf(a) - priceOf(b));
            break;
          case 'price-descending':
            ordered = cards.slice().sort((a, b) => priceOf(b) - priceOf(a));
            break;
          case 'title-ascending':
            ordered = cards.slice().sort((a, b) => titleOf(a).localeCompare(titleOf(b)));
            break;
          case 'best-selling':
          default:
            ordered = this.originalCardOrder.filter((card) => cards.indexOf(card) !== -1);
            break;
        }

        ordered.forEach((card) => this.grid.appendChild(card));
        if (this.bestsellerTile) {
          // Grid slot comes from data-grid-position (set once in the Liquid).
          const position = parseInt(this.bestsellerTile.dataset.gridPosition, 10) || 3;
          const pivot = ordered[position - 1];
          if (pivot) this.grid.insertBefore(this.bestsellerTile, pivot);
          else this.grid.appendChild(this.bestsellerTile);
        }
      }

      /* ---- State mutations (unlimited — there is no maximum) ---- */

      // No-op if already present.
      addProduct(id) {
        if (this.insertProduct(id)) this.render();
      }

      // Adds at qty 1; returns whether anything changed, no render.
      insertProduct(id) {
        if (!id || this.getItem(id)) return false;
        const product = this.catalog.get(String(id));
        // Sold-out products render greyed out with no Add control
        if (!product || !product.available) return false;
        this.state.items.push({ id: String(id), variantId: product.variantId, quantity: 1 });
        return true;
      }

      // Adds at qty 1 if absent; returns whether it changed, no render.
      incrementProduct(id) {
        if (!id || !this.isAvailable(id)) return false;
        const item = this.getItem(id);
        if (item) {
          item.quantity += 1;
          return true;
        }
        return this.insertProduct(id);
      }

      // Removes the item at qty 1; returns whether it changed, no render.
      decrementProduct(id) {
        if (!id) return false;
        const item = this.getItem(id);
        if (!item) return false;
        if (item.quantity <= 1) {
          this.state.items = this.state.items.filter((entry) => entry.id !== String(id));
        } else {
          item.quantity -= 1;
        }
        return true;
      }

      // The tile's "quantity" is how many best-seller sets are in the bundle.
      addBestsellers(trigger) {
        this.setBestsellerCount(this.state.bestsellerCount + 1, trigger);
      }

      /* Steps each configured best-seller by ±1 per set — these variants can also be added
       * individually, so shared quantities get nudged. Variant IDs: nearest [data-variant-ids]. */
      setBestsellerCount(next, trigger) {
        const target = Math.max(0, next);
        const delta = target - this.state.bestsellerCount;
        if (delta === 0) return;
        const variantIds = this.bestsellerVariantIds(trigger);
        if (!variantIds.length) return;
        variantIds.forEach((variantId) => {
          const productId = this.productIdForVariant(variantId);
          if (!productId) return;
          const step = delta > 0 ? this.incrementProduct.bind(this) : this.decrementProduct.bind(this);
          for (let i = 0; i < Math.abs(delta); i += 1) step(productId);
        });
        this.state.bestsellerCount = target;
        this.render();
      }

      bestsellerVariantIds(source) {
        const el = (source && source.closest && source.closest('[data-variant-ids]')) || this.bestsellerTile;
        if (!el) return [];
        return (el.dataset.variantIds || '')
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean);
      }

      // Reverse lookup variant ID → product ID, built lazily from the catalog.
      productIdForVariant(variantId) {
        if (!this.variantIndex) {
          this.variantIndex = new Map();
          this.catalog.forEach((product, id) => {
            if (product.variantId) this.variantIndex.set(String(product.variantId), id);
          });
        }
        return this.variantIndex.get(String(variantId)) || null;
      }

      removeProduct(id) {
        if (!id) return;
        const before = this.state.items.length;
        this.state.items = this.state.items.filter((item) => item.id !== String(id));
        if (this.state.items.length !== before) this.render();
      }

      // A positive delta on an absent product adds it; dropping to 0 removes it.
      updateQuantity(id, delta) {
        if (!id) return;
        const item = this.getItem(id);
        if (!item) {
          if (delta > 0) this.addProduct(id);
          return;
        }
        const next = item.quantity + delta;
        if (next <= 0) {
          this.removeProduct(id);
          return;
        }
        item.quantity = next;
        this.render();
      }

      // Built once from the rendered cards; the best-sellers tile (no data-product-id) is skipped.
      buildCatalog() {
        if (!this.grid) return;
        this.grid.querySelectorAll('[data-product-id]').forEach((card) => {
          const id = card.dataset.productId;
          if (!id || this.catalog.has(id)) return;
          const image = card.querySelector('.bundle-card__image');
          const title = card.querySelector('.bundle-card__title');
          this.catalog.set(id, {
            id,
            handle: card.dataset.productHandle || '',
            variantId: card.dataset.variantId || '',
            available: card.dataset.available !== 'false',
            price: parseInt(card.dataset.price, 10) || 0,
            category: card.dataset.category || '',
            title: title ? title.textContent.trim() : '',
            image: image ? image.currentSrc || image.src : '',
          });
        });
      }

      /* ---- State selectors (read-only helpers) ---- */

      getItem(id) {
        return this.state.items.find((item) => item.id === String(id));
      }

      isAvailable(id) {
        const product = this.catalog.get(String(id));
        return !!product && product.available;
      }

      totalCount() {
        return this.state.items.reduce((sum, item) => sum + item.quantity, 0);
      }

      // Full-price subtotal in minor units (pennies).
      subtotal() {
        return this.state.items.reduce((sum, item) => {
          const product = this.catalog.get(item.id);
          return sum + (product ? product.price * item.quantity : 0);
        }, 0);
      }

      /* ---- Render: DOM is always derived from state ---- */

      render() {
        this.renderCards();
        this.renderBestsellerTile();
        this.renderDrawers();
        this.renderPanel();
        this.collapseIdleCardBanners();
        this.saveState();
      }

      /* ---- Persistence (localStorage + TTL — survives tab/webview death, expires after storageTtl) ---- */

      saveState() {
        try {
          localStorage.setItem(
            this.storageKey,
            JSON.stringify({
              items: this.state.items,
              bestsellerCount: this.state.bestsellerCount,
              savedAt: Date.now(),
            })
          );
        } catch (e) {
          /* private mode / quota — persistence is best-effort, never fatal */
        }
      }

      /* Validated against the catalog: unknown or sold-out items dropped, quantities coerced to >= 1,
       * duplicates merged, variant ids re-read from the catalog (authoritative). */
      restoreState() {
        let raw;
        try {
          // sessionStorage fallback: drafts saved before the localStorage switch.
          raw = localStorage.getItem(this.storageKey) || sessionStorage.getItem(this.storageKey);
        } catch (e) {
          return;
        }
        if (!raw) return;

        let parsed;
        try {
          parsed = JSON.parse(raw);
        } catch (e) {
          return;
        }

        // Legacy format was a bare items array; the current format is { items, bestsellerCount, savedAt }.
        let storedItems = parsed;
        if (!Array.isArray(parsed)) {
          if (!parsed || typeof parsed !== 'object') return;
          if (parsed.savedAt && Date.now() - parsed.savedAt > this.storageTtl) {
            this.clearState();
            return;
          }
          storedItems = parsed.items;
          this.state.bestsellerCount = Math.max(0, Math.floor(Number(parsed.bestsellerCount) || 0));
        }
        if (!Array.isArray(storedItems)) return;

        const merged = new Map();
        storedItems.forEach((entry) => {
          if (!entry) return;
          const id = String(entry.id);
          const product = this.catalog.get(id);
          if (!product || !product.available) return;
          const quantity = Math.max(1, Math.floor(Number(entry.quantity) || 0));
          if (merged.has(id)) merged.get(id).quantity += quantity;
          else merged.set(id, { id, variantId: product.variantId, quantity });
        });
        this.state.items = Array.from(merged.values());
      }

      clearState() {
        try {
          localStorage.removeItem(this.storageKey);
          sessionStorage.removeItem(this.storageKey);
        } catch (e) {
          /* non-fatal */
        }
      }

      // Reflect state onto each product card: in-bundle flag + stepper quantity.
      renderCards() {
        if (!this.grid) return;
        this.grid.querySelectorAll('[data-product-id]').forEach((card) => {
          const item = this.getItem(card.dataset.productId);
          card.dataset.inBundle = item ? 'true' : 'false';
          const qty = card.querySelector('[data-bundle-qty]');
          if (qty) qty.textContent = item ? item.quantity : 1;
        });
      }

      // Mirrors renderCards() for the one tile that has no product id.
      renderBestsellerTile() {
        if (!this.bestsellerTile) return;
        const count = this.state.bestsellerCount;
        this.bestsellerTile.dataset.inBundle = count > 0 ? 'true' : 'false';
        const qty = this.bestsellerTile.querySelector('[data-bundle-qty]');
        if (qty) qty.textContent = count > 0 ? count : 1;
      }

      // Shows the true count — 0 when not in bundle — so the first "+" visibly goes 0 → 1.
      renderDrawers() {
        this.querySelectorAll('[data-bundle-drawer]').forEach((drawer) => {
          const qty = drawer.querySelector('[data-bundle-qty]');
          if (!qty) return;
          // The Best Sellers drawer tracks the set count, not a catalog item.
          if (drawer.hasAttribute('data-bundle-bestseller-tile')) {
            qty.textContent = this.state.bestsellerCount;
            return;
          }
          const id = drawer.id.slice('BundleDrawer-'.length);
          const item = this.getItem(id);
          qty.textContent = item ? item.quantity : 0;
        });
      }

      renderPanel() {
        if (this.panelCount) this.panelCount.textContent = this.totalCount();
        // Discounted subtotal is display only — Shopify applies the real discount at checkout.
        if (this.panelSubtotal) {
          this.panelSubtotal.textContent = this.formatMoney(this.subtotal() - this.bundleSaving());
        }
        this.renderPanelItems();
        this.updateProgress();
        this.updateDiscountDisplay();
      }

      // Per-unit £ off (pennies), FLOORED — mirrors Shopify's checkout rounding (verify Phase 6).
      unitSaving(unitPrice) {
        if (this.discountPercentage <= 0) return 0;
        return Math.floor((unitPrice * this.discountPercentage) / 100);
      }

      // Total £ saving — per-unit saving × qty, summed (NOT a % of the aggregate subtotal,
      // which drifts pennies from what checkout charges).
      bundleSaving() {
        return this.state.items.reduce((sum, item) => {
          const product = this.catalog.get(item.id);
          return sum + (product ? this.unitSaving(product.price) * item.quantity : 0);
        }, 0);
      }

      /* minQuantity unlocks the discount/CTA; shippingQuantity is the progress target and
       * "complete" point. Unlimited beyond that. */
      updateProgress() {
        const count = this.totalCount();
        const toMin = Math.max(this.minQuantity - count, 0);
        // Free shipping is price-based on the DISCOUNTED subtotal (what the customer pays).
        const discountedSubtotal = this.subtotal() - this.bundleSaving();
        const freeShipping =
          this.shippingThreshold > 0 && discountedSubtotal >= this.shippingThreshold;

        // Segment bar: proportional fill so it works for any segment count / shippingQuantity.
        if (this.segments.length) {
          const ratio = this.shippingQuantity > 0 ? Math.min(count / this.shippingQuantity, 1) : 0;
          const filled = Math.round(ratio * this.segments.length);
          this.segments.forEach((seg, i) => seg.classList.toggle('is-filled', i < filled));
        }

        const ready = count >= this.minQuantity;
        if (this.ctaButton) this.ctaButton.disabled = !ready;
        if (this.ctaLabel) {
          this.ctaLabel.textContent = ready ? this.ctaReadyLabel : `Add ${toMin} more to items`;
        }

        // "Remaining" counts toward the same target as the CTA (minQuantity).
        if (this.statusAdded) {
          this.statusAdded.textContent = `${count} ${count === 1 ? 'item' : 'items'} added`;
        }
        if (this.statusRemaining) {
          this.statusRemaining.textContent = `${toMin} more to add`;
          this.statusRemaining.hidden = ready;
        }
        if (this.statusReady) this.statusReady.hidden = !ready;
        if (this.statusShipping) this.statusShipping.hidden = !freeShipping;

        // Milestone dots (free shipping is price-based; the gift is pot-count, display-only).
        this.milestones.forEach((el) => {
          const type = el.dataset.bundleMilestone;
          let reached = false;
          if (type === 'discount') reached = count >= this.minQuantity;
          else if (type === 'shipping') reached = freeShipping;
          else if (type === 'gift') reached = this.giftQuantity > 0 && count >= this.giftQuantity;
          el.classList.toggle('is-reached', reached);
        });
      }

      // Saving badge is display only — Shopify applies the real discount at checkout.
      updateDiscountDisplay() {
        if (!this.savingEl) return;
        const saving = this.bundleSaving();
        if (saving > 0) {
          this.savingEl.textContent = `Saving ${this.formatMoney(saving)}`;
          this.savingEl.hidden = false;
        } else {
          this.savingEl.hidden = true;
          this.savingEl.textContent = '';
        }
      }

      /* Item rows + "available slot" placeholders (max 3) up to the minimum. Quantity-only
       * changes update rows in place so a clicked stepper isn't destroyed mid-interaction. */
      renderPanelItems() {
        if (!this.panelItems) return;

        const ids = this.state.items.map((item) => item.id);
        const rendered = Array.from(this.panelItems.querySelectorAll('[data-bundle-item]')).map(
          (el) => el.dataset.bundleItem
        );
        const sameMembership =
          ids.length === rendered.length && ids.every((id, i) => id === rendered[i]);

        if (sameMembership) {
          this.state.items.forEach((item) => this.updateItemRow(item));
        } else {
          this.panelItems.replaceChildren();
          this.state.items.forEach((item) => this.panelItems.appendChild(this.itemElement(item)));
        }
        this.syncSlots();
      }

      syncSlots() {
        const desired = Math.min(3, Math.max(0, this.minQuantity - this.totalCount()));
        const slots = this.panelItems.querySelectorAll('[data-bundle-slot]');
        for (let i = slots.length - 1; i >= desired; i -= 1) slots[i].remove();
        for (let i = slots.length; i < desired; i += 1) {
          this.panelItems.appendChild(this.slotElement());
        }
      }

      // In-place qty refresh for an existing panel row (unit price doesn't change with qty).
      updateItemRow(item) {
        const row = this.panelItems.querySelector(`[data-bundle-item="${item.id}"]`);
        if (!row) return;
        const qty = row.querySelector('[data-bundle-qty]');
        if (qty) qty.textContent = item.quantity;
      }

      slotElement() {
        const li = document.createElement('li');
        li.className = 'bundle-panel__slot';
        li.dataset.bundleSlot = '';

        const plus = document.createElement('span');
        plus.className = 'bundle-panel__slot-plus';
        plus.setAttribute('aria-hidden', 'true');
        plus.textContent = '+';

        const label = document.createElement('span');
        label.className = 'bundle-panel__slot-label bb-p-bold';
        label.textContent = 'Available slot';

        li.append(plus, label);
        return li;
      }

      // One selected-item row, built with DOM APIs (not innerHTML) so product titles stay inert.
      itemElement(item) {
        const product = this.catalog.get(item.id) || {};
        const li = document.createElement('li');
        li.className = 'bundle-panel__item';
        li.dataset.bundleItem = item.id;

        // Media
        const media = document.createElement('div');
        media.className = 'bundle-panel__item-media';
        if (product.image) {
          const img = document.createElement('img');
          img.className = 'bundle-panel__item-image';
          img.src = product.image;
          img.alt = '';
          img.loading = 'lazy';
          media.appendChild(img);
        }

        // Info: title + unit price (bundle-discounted display, original struck through)
        const info = document.createElement('div');
        info.className = 'bundle-panel__item-info';
        const title = document.createElement('p');
        title.className = 'bundle-panel__item-title bb-p-bold bb-p-sm';
        title.textContent = product.title || '';
        const price = document.createElement('p');
        price.className = 'bundle-panel__item-price bb-p-sm';
        const unitPrice = product.price || 0;
        if (this.discountPercentage > 0) {
          const was = document.createElement('s');
          was.className = 'bundle-panel__item-price-was';
          was.textContent = this.formatMoney(unitPrice);
          const now = document.createElement('span');
          now.className = 'bundle-panel__item-price-now bb-p-bold';
          now.textContent = this.formatMoney(unitPrice - this.unitSaving(unitPrice));
          price.append(was, ' ', now);
        } else {
          price.textContent = this.formatMoney(unitPrice);
        }
        info.append(title, price);

        // Stepper
        const stepper = document.createElement('div');
        stepper.className = 'bundle-panel__item-stepper';
        stepper.dataset.bundleStepper = '';
        const decrease = document.createElement('button');
        decrease.type = 'button';
        decrease.className = 'bundle-card__step';
        decrease.dataset.bundleDecrease = '';
        decrease.setAttribute('aria-label', `Decrease quantity of ${product.title || 'item'}`);
        decrease.innerHTML = '&minus;';
        const qty = document.createElement('span');
        qty.className = 'bundle-card__qty bb-p-bold';
        qty.dataset.bundleQty = '';
        qty.setAttribute('aria-live', 'polite');
        qty.textContent = item.quantity;
        const increase = document.createElement('button');
        increase.type = 'button';
        increase.className = 'bundle-card__step';
        increase.dataset.bundleIncrease = '';
        increase.setAttribute('aria-label', `Increase quantity of ${product.title || 'item'}`);
        increase.textContent = '+';
        stepper.append(decrease, qty, increase);

        li.append(media, info, stepper);
        return li;
      }

      /* Index stored on the carousel root so delegated clicks cover template-hydrated drawers.
       * Prev/next wrap; dots jump straight to a slide. */
      moveCarousel(control) {
        const root = control.closest('[data-bundle-carousel]');
        const track = root && root.querySelector('[data-bundle-carousel-track]');
        if (!track || !track.children.length) return;
        const count = track.children.length;
        let index = parseInt(root.dataset.carouselIndex, 10) || 0;
        if (control.hasAttribute('data-bundle-carousel-prev')) index -= 1;
        else if (control.hasAttribute('data-bundle-carousel-next')) index += 1;
        else index = parseInt(control.dataset.carouselIndex, 10) || 0;
        index = ((index % count) + count) % count;
        root.dataset.carouselIndex = index;
        track.style.transform = `translateX(-${index * 100}%)`;
        root.querySelectorAll('[data-bundle-carousel-dot]').forEach((dot, i) => {
          dot.classList.toggle('is-active', i === index);
        });
      }

      /* ---- Detail drawer (reuses Dawn's trapFocus/removeTrapFocus) ---- */

      openDrawer(id) {
        const drawer = document.getElementById(id) || this.hydrateDrawer(id);
        if (!drawer) return;
        if (this.activeDrawer) this.closeDrawer();
        this.drawerOpener = document.activeElement;
        this.activeDrawer = drawer;
        drawer.hidden = false;
        document.body.classList.add('overflow-hidden');
        const focusTarget = drawer.querySelector('[data-bundle-drawer-close]') || drawer;
        trapFocus(drawer, focusTarget);
      }

      /* Drawers ship as inert <template>s, cloned on first open then reused by id —
       * ~one drawer's worth of nodes instead of one per product. */
      hydrateDrawer(id) {
        const template = document.getElementById(`${id}--template`);
        const container = this.querySelector('[data-bundle-drawers]');
        if (!template || !container) return null;
        container.appendChild(template.content.cloneNode(true));
        const drawer = document.getElementById(id);
        if (!drawer) return null;
        // Late bindings: connectedCallback ran before this drawer existed.
        drawer.addEventListener('click', this.boundDrawerDismiss);
        this.drawerRoots.push(drawer);
        this.renderDrawers(); // sync the fresh stepper to the current bundle quantity
        return drawer;
      }

      closeDrawer() {
        if (!this.activeDrawer) return;
        this.activeDrawer.hidden = true;
        this.activeDrawer = null;
        document.body.classList.remove('overflow-hidden');
        // preventScroll: focusing the clipped-below-media Learn more button otherwise
        // scrolls the overflow-hidden card media, shifting the image up permanently.
        removeTrapFocus();
        if (this.drawerOpener) {
          this.drawerOpener.focus({ preventScroll: true });
          const media = this.drawerOpener.closest('.bundle-card__media');
          if (media) media.scrollTop = 0;
        }
        this.drawerOpener = null;
      }

      /* ---- Mobile bottom tray (<details>) ---- */

      // Collapse on mobile; keep the <details> open on desktop (a closed <details> hides its body).
      setInitialTrayState() {
        if (!this.panelDisclosure) return;
        this.panelDisclosure.open = !this.trayQuery.matches;
      }

      handleTrayBreakpoint(event) {
        if (this.panelDisclosure) this.panelDisclosure.open = !event.matches;
      }

      /* ---- Cart submit ---- */

      /* POST the bundle to /cart/add.js, then open the cart drawer in place (fallback:
       * navigate to /cart). `_bundle_*` properties are hidden (underscore-prefixed) —
       * grouping only; Shopify applies the real discount at checkout. */
      checkout() {
        if (this.submitting || this.totalCount() < this.minQuantity) return;
        // Ignore a click within 500ms of a tray open/close — it's a ghost click, not a real tap.
        if (this.lastTrayToggle && Date.now() - this.lastTrayToggle < 500) return;
        this.submitting = true;
        this.setCtaLoading(true);

        const staleError = this.querySelector('[data-bundle-cart-error]');
        if (staleError) staleError.textContent = '';

        const bundleId = `bundle-${Date.now()}`;
        // Read before clearState(); the attribute feeds the Flow order-tag rule (fulfilment).
        const giftEligible = this.giftQuantity > 0 && this.totalCount() >= this.giftQuantity;
        const items = this.state.items.map((item) => ({
          id: Number(item.variantId),
          quantity: item.quantity,
          properties: {
            _bundle_id: bundleId,
            _bundle_discount: `${this.discountPercentage}%`,
          },
        }));

        const config = fetchConfig('json');
        const cartDrawer = document.querySelector('cart-drawer');
        const payload = { items };
        if (cartDrawer) {
          payload.sections = cartDrawer.getSectionsToRender().map((section) => section.id);
          payload.sections_url = window.location.pathname;
        }
        config.body = JSON.stringify(payload);

        fetch(`${window.routes.cart_add_url}`, config)
          .then((response) =>
            response.text().then((text) => {
              try {
                return { ok: response.ok, data: JSON.parse(text) };
              } catch (e) {
                console.error('[bundle-builder] cart/add returned non-JSON', response.status, text.slice(0, 200));
                return { ok: false, data: { description: CART_UNREACHABLE } };
              }
            })
          )
          .then(({ ok, data }) => {
            if (!ok || data.status) {
              this.handleCartError(data);
              return;
            }
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bundle-builder', cartData: data });
            }
            // The bundle is now in the cart — clear the saved draft so it isn't re-added.
            this.clearState();
            const finalize = () => {
              if (cartDrawer && data.sections) {
                // Reset the builder UI (state is already cleared) and show the drawer in place.
                this.state.items = [];
                this.submitting = false;
                this.setCtaLoading(false);
                this.render();
                try {
                  cartDrawer.renderContents(data);
                  return;
                } catch (e) {
                  // The add succeeded — fall through to /cart rather than report a failed purchase.
                  console.error('[bundle-builder] cart drawer render failed', e);
                }
              }
              window.location.href = window.routes.cart_url || '/cart';
            };
            if (giftEligible && window.routes.cart_update_url) {
              // Set-only (never downgraded): a Flow rule tags orders where this equals Yes.
              const attrConfig = fetchConfig('json');
              attrConfig.body = JSON.stringify({ attributes: { 'Free gift eligible': 'Yes' } });
              fetch(`${window.routes.cart_update_url}`, attrConfig)
                .catch(() => {})
                .then(finalize);
              return;
            }
            finalize();
          })
          .catch((error) => {
            console.error('[bundle-builder] add to cart failed', error);
            this.handleCartError({ description: CART_UNREACHABLE });
          });
      }

      setCtaLoading(loading) {
        if (!this.ctaButton) return;
        this.ctaButton.disabled = loading;
        if (loading && this.ctaLabel) this.ctaLabel.textContent = 'Adding…';
      }

      handleCartError(data) {
        this.submitting = false;
        this.setCtaLoading(false);
        this.updateProgress(); // restores the CTA label + enabled state from current count
        console.error('[bundle-builder] cart error', data);
        const message = (data && (data.description || data.message)) || CART_UNREACHABLE;
        let errorEl = this.querySelector('[data-bundle-cart-error]');
        if (!errorEl) {
          errorEl = document.createElement('p');
          errorEl.className = 'bundle-panel__error bb-p-sm';
          errorEl.setAttribute('role', 'alert');
          errorEl.setAttribute('data-bundle-cart-error', '');
          if (this.ctaButton && this.ctaButton.parentNode) {
            this.ctaButton.parentNode.insertBefore(errorEl, this.ctaButton.nextSibling);
          }
        }
        errorEl.textContent = message;
      }

      /* ---- Utilities ---- */

      // Minor units (pennies) → localised currency string. Display only.
      formatMoney(cents) {
        const amount = (cents || 0) / 100;
        try {
          return new Intl.NumberFormat(this.locale, {
            style: 'currency',
            currency: this.currency,
          }).format(amount);
        } catch (e) {
          return `£${amount.toFixed(2)}`;
        }
      }
    }
  );
}
